import React from 'react';
import {Platform, PermissionsAndroid, Share} from 'react-native';
import WebPdf, {WebPdfProps, WebPdfSource} from './platformHelpers/webPdf';
import {marked} from 'marked';

// Import the native PDF component only on native platforms
let NativePdf: any = null;
let RNHTMLtoPDF: any = null;
if (Platform.OS !== 'web') {
  // Use require instead of import to avoid webpack issues
  NativePdf = require('react-native-pdf').default;
  RNHTMLtoPDF = require('react-native-html-to-pdf').default;
}

export type PdfSource = WebPdfSource;
export type PdfProps = WebPdfProps;

interface GeneratePdfOptions {
  html: string;
  fileName: string;
  directory?: string;
  base64?: boolean;
  width?: number;
  height?: number;
  padding?: number;
  customHeader?: string;
  customFooter?: string;
}

interface PdfResult {
  filePath?: string;
  base64?: string;
  width?: number;
  height?: number;
  message?: string;
}

/**
 * Cross-platform PDF viewer component for React Native and React Native Web
 * Uses react-native-pdf on native platforms and iframe/object elements on web
 */
const Pdf: React.FC<PdfProps> = props => {
  if (Platform.OS === 'web') {
    return <WebPdf {...props} />;
  } else {
    // Use native PDF component on mobile platforms
    return <NativePdf {...props} />;
  }
};

/**
 * Request write storage permission on Android
 * @returns {Promise<boolean>} Whether permission was granted
 */
const requestWritePermission = async (): Promise<boolean> => {
  if (Platform.OS !== 'android') return true;

  try {
    const granted = await PermissionsAndroid.request(
      PermissionsAndroid.PERMISSIONS.WRITE_EXTERNAL_STORAGE,
      {
        title: 'Storage Permission',
        message: 'Application needs access to your storage to save PDF files',
        buttonNeutral: 'Ask Me Later',
        buttonNegative: 'Cancel',
        buttonPositive: 'OK',
      },
    );
    return granted === PermissionsAndroid.RESULTS.GRANTED;
  } catch (err) {
    return false;
  }
};

/**
 * Generate a PDF from HTML content
 * @param {GeneratePdfOptions} options PDF generation options
 * @returns {Promise<PdfResult>} Result of PDF generation
 */
export const generatePdf = async (
  options: GeneratePdfOptions,
): Promise<PdfResult> => {
  // Different implementation for web and native
  if (Platform.OS === 'web') {
    return generatePdfWeb(options);
  } else {
    const hasPermission = await requestWritePermission();
    if (!hasPermission) {
      throw new Error('Storage permission denied');
    }
    return generatePdfNative(options);
  }
};

/**
 * Generate PDF on web platform
 */
const generatePdfWeb = async (
  options: GeneratePdfOptions,
): Promise<PdfResult> => {
  try {
    // For web, we'll use the browser's print functionality
    // Create a new window instead of iframe to avoid document.write issues
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      throw new Error('Could not open print window');
    }

    // Set the content using innerHTML instead of document.write
    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${options.fileName}</title>
          <style>
            @page {
              size: A4;
              margin: ${options.padding || 20}px;
            }
            body {
              font-family: Arial, sans-serif;
              color: #333;
              line-height: 1.5;
            }
            @media print {
              .page-break {
                page-break-after: always;
              }
            }
          </style>
        </head>
        <body>
          ${options.html}
        </body>
      </html>
    `);
    printWindow.document.close();

    return new Promise<PdfResult>((resolve, reject) => {
      // Wait for the content to load
      printWindow.onload = () => {
        try {
          // Print the window content as PDF
          printWindow.print();

          // Close the window after printing
          setTimeout(() => {
            printWindow.close();

            // On web, we can't get the actual file path, but we return a success message
            resolve({
              filePath: `${options.fileName}.pdf (downloaded)`,
            });
          }, 1000);
        } catch (error) {
          printWindow.close();
          reject(error);
        }
      };

      // Single fallback if onload doesn't fire - only close window, don't print again
      setTimeout(() => {
        if (!printWindow.closed) {
          printWindow.close();
          resolve({
            filePath: `${options.fileName}.pdf (download cancelled)`,
          });
        }
      }, 3000);
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Generate PDF on native platforms using react-native-html-to-pdf
 */
const generatePdfNative = async (
  options: GeneratePdfOptions,
): Promise<PdfResult> => {
  try {
    if (!RNHTMLtoPDF) {
      throw new Error('PDF generation is not available on this platform');
    }

    const PDFOptions = {
      html: options.html,
      fileName: options.fileName,
      directory:
        options.directory ||
        (Platform.OS === 'android' ? 'Download' : 'Documents'),
      base64: options.base64 || false,
      width: options.width,
      height: options.height,
      padding: options.padding,
    };

    const file = await RNHTMLtoPDF.convert(PDFOptions);

    return {
      filePath: file.filePath,
      base64: file.base64,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Exports chat messages to text format using Share API
 * @param messages - Array of chat messages
 * @returns Promise with share result
 */
export const exportChatToText = async (messages: any[]) => {
  try {
    const messagesCopy = [...(messages || [])].reverse();

    // Create a formatted text representation of the chat
    let chatText = 'Chat Conversation\n\n';

    // Add timestamp
    chatText += `Exported on: ${new Date().toLocaleString()}\n\n`;

    // Process messages in pairs (user question, assistant answer)
    const pairedMessages: {question?: any; answer?: any}[] = [];
    let i = 0;

    while (i < messagesCopy.length) {
      if (i + 1 < messagesCopy.length) {
        const first = messagesCopy[i];
        const second = messagesCopy[i + 1];
        if (first.role === 'assistant' && second.role === 'user') {
          pairedMessages.push({question: second, answer: first});
          i += 2;
          continue;
        }
        if (first.role === 'user' && second.role === 'assistant') {
          pairedMessages.push({question: first, answer: second});
          i += 2;
          continue;
        }
      }
      const msg = messagesCopy[i];
      if (msg.role === 'user') {
        pairedMessages.push({question: msg});
      } else {
        pairedMessages.push({answer: msg});
      }
      i++;
    }

    // Build conversation text
    pairedMessages.forEach((pair, index) => {
      chatText += `--- Conversation ${index + 1} ---\n\n`;

      if (pair.question) {
        chatText += `Question:\n${pair.question.message || ''}\n\n`;
      }

      if (pair.answer) {
        chatText += `Answer:\n${pair.answer.message || ''}\n\n`;
      }

      chatText += '\n';
    });

    // Share the text content
    await Share.share({
      message: chatText,
      title: 'Chat Export',
    });

    return {success: true, message: 'Chat exported as text'};
  } catch (error) {
    throw error;
  }
};

// Export state to prevent multiple rapid exports
let isExporting = false;

/**
 * Exports chat messages to PDF
 * @param messages - Array of chat messages
 * @param isRTL - Whether the content should be right-to-left
 * @returns Promise with PDF generation result
 */
export const exportChatToPdf = async (
  messages: any[],
  isRTL: boolean = false,
): Promise<PdfResult> => {
  // Prevent multiple rapid export attempts
  if (isExporting) {
    return {
      filePath: 'Export already in progress',
      message: 'Please wait for the current export to complete.',
    };
  }

  isExporting = true;
  
  try {
    // For React Native, we'll use the Share API as a fallback if PDF generation fails
    if (Platform.OS !== 'web') {
      try {
        // Try to generate PDF first
        const htmlContent = createChatHtmlContent(messages, isRTL);
        const result = await generatePdf({
          html: htmlContent,
          fileName: `chat_export_${new Date().toISOString().split('T')[0]}`,
          padding: 20,
        });

        return result;
      } catch (pdfError) {
        // Fallback to text export using Share API
        const textResult = await exportChatToText(messages);
        return {
          filePath: 'chat_export.txt',
          message: textResult.message || 'Chat exported as text',
        };
      }
    } else {
      // Web platform - try multiple approaches
      try {
        // First try: Use window.open method
        const htmlContent = createChatHtmlContent(messages, isRTL);
        const result = await generatePdf({
          html: htmlContent,
          fileName: `chat_export_${new Date().toISOString().split('T')[0]}`,
          padding: 20,
        });
        return result;
      } catch (webError) {
        // Second try: Create a downloadable HTML file
        try {
          const htmlContent = createChatHtmlContent(messages, isRTL);
          const blob = new Blob([htmlContent], {type: 'text/html'});
          const url = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = url;
          link.download = `chat_export_${new Date().toISOString().split('T')[0]}.html`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          return {
            filePath: `chat_export_${new Date().toISOString().split('T')[0]}.html (downloaded)`,
            message:
              'HTML file downloaded. You can open it in a browser and print as PDF.',
          };
        } catch (downloadError) {
          // Final fallback: text export
          const textResult = await exportChatToText(messages);
          return {
            filePath: 'chat_export.txt',
            message: textResult.message || 'Chat exported as text',
          };
        }
      }
    }
  } catch (error) {
    throw error;
  } finally {
    // Always reset the export state
    isExporting = false;
  }
};

/**
 * Creates HTML content for chat messages with beautiful styling and enhanced markdown support using marked library
 * @param messages - Array of chat messages
 * @param isRTL - Whether the content should be right-to-left
 * @returns HTML string
 */
const createChatHtmlContent = (
  messages: any[],
  isRTL: boolean = false,
): string => {
  const direction = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'right' : 'left';

  // Configure marked options for better parsing
  marked.setOptions({
    breaks: true, // Convert line breaks to <br>
    gfm: true, // GitHub Flavored Markdown
  });

  const messagesHtml = messages
    .map((message, index) => {
      const isUser = message.role === 'user';
      const messageClass = isUser ? 'user-message' : 'assistant-message';
      const timestamp = new Date(
        message.created_at || Date.now(),
      ).toLocaleString();
      const messageContent =
        message.content || message.text || message.message || '';

      // Parse markdown using marked library
      let parsedContent;
      try {
        parsedContent = marked(messageContent);
      } catch (error) {
        parsedContent = messageContent.replace(/\n/g, '<br>');
      }

      return `
      <div class="message ${messageClass}">
        <div class="message-bubble">
          <div class="message-header">
            <div class="message-role">
            </div>
            <span class="message-timestamp">${timestamp}</span>
          </div>
          <div class="message-content">
            ${parsedContent}
          </div>
        </div>
      </div>
    `;
    })
    .join('');

  return `
    <!DOCTYPE html>
    <html dir="${direction}" lang="en">
    <head>
      <meta charset="UTF-8">
      <title>GenioMD Chat Export</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1.6;
          color: #333333;
          background-color: #F8F8FF;
          margin: 0;
          padding: 0;
          direction: ${direction};
          text-align: ${textAlign};
        }
        
        .container {
          max-width: 800px;
          margin: 0 auto;
          background-color: #FFFFFF;
          min-height: 100vh;
          box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
        }
        
        .header {
          background: linear-gradient(135deg, #6633FF 0%, #5A20CB 100%);
          color: #FFFFFF;
          padding: 40px 30px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        
        .header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
          opacity: 0.3;
        }
        
        .header-content {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        }
        
        .logo {
          width: 80px;
          height: 80px;
          background: url('logo.png') center/contain no-repeat;
          border-radius: 50%;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
        }
        
        /* Fallback for logo */
        .logo:not([style*="logo.png"]) {
          background: linear-gradient(135deg, #6633FF 0%, #5A20CB 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
          font-weight: bold;
        }
        
        .header h1 {
          font-size: 32px;
          font-weight: 700;
          margin: 0;
          letter-spacing: -0.5px;
        }
        
        .header .subtitle {
          font-size: 16px;
          font-weight: 400;
          opacity: 0.9;
          margin: 0;
        }
        
        .header .metadata {
          font-size: 14px;
          opacity: 0.8;
          margin: 0;
        }
        
        .chat-container {
          padding: 30px;
          background-color: #FFFFFF;
        }
        
        .message {
          margin-bottom: 24px;
          display: flex;
          flex-direction: column;
        }
        
        .user-message {
          align-items: flex-end;
        }
        
        .assistant-message {
          align-items: flex-start;
        }
        
        .message-bubble {
          max-width: 70%;
          position: relative;
        }
        
        .message-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          font-size: 12px;
          font-weight: 500;
        }
        
        .message-role {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #6633FF;
          font-weight: 600;
        }
        
        .role-icon {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          object-fit: cover;
          border: 1px solid #E0E0E0;
        }
        
        /* Fallback for when images don't load */
        .message-role .role-icon:not([src]),
        .message-role .role-icon[src=""],
        .message-role .role-icon[src*="data:"] {
          display: none;
        }
        
        .message-timestamp {
          color: #999;
          font-size: 11px;
        }
        
        .message-content {
          padding: 16px 20px;
          border-radius: 20px;
          font-size: 15px;
          line-height: 1.5;
          word-wrap: break-word;
          white-space: pre-wrap;
        }
        
        .user-message .message-content {
          background: linear-gradient(135deg, #6633FF 0%, #5A20CB 100%);
          color: #FFFFFF;
          border-bottom-right-radius: 4px;
        }
        
        .assistant-message .message-content {
          background-color: #FFFFFF;
          color: #333333;
          border: 1px solid #E5E5E5;
          border-bottom-left-radius: 4px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }
        
        .footer {
          background-color: #F8F8FF;
          padding: 20px 30px;
          text-align: center;
          border-top: 1px solid #E5E5E5;
        }
        
        .footer p {
          color: #707070;
          font-size: 14px;
          margin: 0;
        }
        
        /* Enhanced markdown styles */
        .message-content h1, .message-content h2, .message-content h3,
        .message-content h4, .message-content h5, .message-content h6 {
          margin: 16px 0 8px 0;
          color: inherit;
          line-height: 1.3;
        }
        
        .message-content h1 {
          font-size: 20px;
          font-weight: 700;
        }
        
        .message-content h2 {
          font-size: 18px;
          font-weight: 600;
        }
        
        .message-content h3 {
          font-size: 16px;
          font-weight: 600;
        }
        
        .message-content h4, .message-content h5, .message-content h6 {
          font-size: 14px;
          font-weight: 600;
        }
        
        .message-content p {
          margin: 8px 0;
        }
        
        .message-content ul, .message-content ol {
          margin: 12px 0;
          padding-left: 24px;
        }
        
        .message-content li {
          margin: 4px 0;
        }
        
        .message-content blockquote {
          margin: 16px 0;
          padding: 12px 20px;
          background-color: #F8F8FF;
          border-left: 4px solid #6633FF;
          font-style: italic;
          color: #666;
        }
        
        .message-content pre {
          margin: 16px 0;
          padding: 16px;
          background-color: #F5F5F5;
          border: 1px solid #E0E0E0;
          border-radius: 8px;
          overflow-x: auto;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 14px;
          line-height: 1.4;
        }
        
        .message-content code {
          background-color: #F5F5F5;
          border: 1px solid #E0E0E0;
          border-radius: 4px;
          padding: 2px 6px;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 13px;
        }
        
        .message-content a {
          color: #6633FF;
          text-decoration: none;
          border-bottom: 1px solid #6633FF;
        }
        
        /* Table styles */
        .message-content table {
          width: 100%;
          border-collapse: collapse;
          border: 1px solid #E0E0E0;
          border-radius: 8px;
          overflow: hidden;
          margin: 16px 0;
          font-size: 14px;
        }
        
        .message-content th {
          background-color: #F8F8FF;
          color: #6633FF;
          font-weight: 600;
          padding: 12px 16px;
          text-align: left;
          border-bottom: 2px solid #E0E0E0;
          border-right: 1px solid #E0E0E0;
        }
        
        .message-content th:last-child {
          border-right: none;
        }
        
        .message-content td {
          padding: 12px 16px;
          border-bottom: 1px solid #E0E0E0;
          border-right: 1px solid #E0E0E0;
          vertical-align: top;
        }
        
        .message-content td:last-child {
          border-right: none;
        }
        
        .message-content tr:nth-child(even) {
          background-color: #FAFAFA;
        }
        
        .message-content tr:hover {
          background-color: #F0F0F0;
        }
        
        .message-content tr:last-child td {
          border-bottom: none;
        }
        
        /* Horizontal rule */
        .message-content hr {
          border: none;
          border-top: 2px solid #E0E0E0;
          margin: 24px 0;
        }
        
        /* Inline code within text */
        .message-content p code,
        .message-content li code {
          background-color: #F5F5F5;
          border: 1px solid #E0E0E0;
          border-radius: 4px;
          padding: 2px 6px;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 13px;
          color: #D63384;
        }
        
        /* Lists with better spacing */
        .message-content ul ul,
        .message-content ol ol,
        .message-content ul ol,
        .message-content ol ul {
          margin: 8px 0;
        }
        
        /* Task lists (GitHub Flavored Markdown) */
        .message-content input[type="checkbox"] {
          margin-right: 8px;
          transform: scale(1.2);
        }
        
        /* Strikethrough text */
        .message-content del {
          text-decoration: line-through;
          color: #666;
        }
        
        @media print {
          body {
            background-color: #FFFFFF;
          }
          
          .container {
            box-shadow: none;
          }
          
          .header {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          
          .user-message .message-content {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <div class="header-content">
            <div class="logo" title="GenioMD">G</div>
            <h1>GenioMD Chat Export</h1>
            <div class="subtitle">Medical AI Assistant Conversation</div>
            <div class="metadata">
              Generated on ${new Date().toLocaleString()}<br>
              Total messages: ${messages.length}
            </div>
          </div>
        </div>
        
        <div class="chat-container">
          ${messagesHtml}
        </div>
        
        <div class="footer">
          <p>© ${new Date().getFullYear()} GenioMD - Medical AI Assistant</p>
        </div>
      </div>
    </body>
    </html>
  `;
};

export default Pdf;
